import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import SubFooter from '../components/SubFooter/SubFooter';
import { ClipLoader } from 'react-spinners';
import AdminLayout from './AdminLayout';
import FlightResults from '../components/FlightResults/FlightResults';
import FlightSearchPage from '../components/FlightSearchPage/FlightSearchPage';
import CancellationPolicy from '../components/CancellationPolicy/CancellationPolicy';
import FlightList from '../components/FlightList/FlightList';
import RedirectEmbed from '../pages/RedirectEmbed';
import { useRedirect } from '../context/RedirectContext';
import { isRedirectShortlinkPath } from '../utils/redirectShortlink';

const Home = lazy(() => import('../components/Home/Home'));
const About = lazy(() => import('../components/About/About'));
const Contact = lazy(() => import('../components/Contact/Contact'));
const Disclaimer = lazy(() => import('../components/Disclaimer/Disclaimer'));
const PrivacyPolicy = lazy(() => import('../components/PrivacyPolicy/PrivacyPolicy'));
const Terms = lazy(() => import('../components/Terms/Terms'));
const FavouriteTravel = lazy(() => import('../components/FavouriteRecipe/FavouriteTravel'));
const TravelShowcase = lazy(() => import('../components/TravelShowcase/TravelShowcase'));
const TravelDestinationList = lazy(() => import('../components/TravelDestinationList/TravelDestinationList'));
const CarRentalList = lazy(() => import('../components/CarRentalList/CarRentalList'));
const HotelBookingList = lazy(() => import('../components/HotelBookingList/HotelBookingList'));

const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);
  return null;
};

const MainContent = ({ children }) => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isAdjustedRoute = ['/services', '/about', '/contact'].includes(location.pathname);
  const style = {
    ...(isAdjustedRoute ? { minHeight: '60vh', display: 'flex', flexDirection: 'column' } : {}),
    ...(!isHome && !isRedirectShortlinkPath(location.pathname) ? { paddingTop: '9rem' } : {}),
  };
  return <div style={style}>{children}</div>;
};

const RouteWithLayout = () => {
  const location = useLocation();
  const { hideSiteChrome } = useRedirect();
  const isAdminPage = location.pathname.includes('admin');
  const showChrome = !isAdminPage && !hideSiteChrome;

  return (
    <>
      {showChrome && <Header />}
      <ScrollToTop />
      <main className={`main-content${hideSiteChrome ? ' main-content--redirect-embed' : ''}`}>
        <div style={!isAdminPage ? { minHeight: hideSiteChrome ? undefined : '70vh' } : {}}>
          <MainContent>
            <Suspense
              fallback={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '50vh',
                  }}
                >
                  <ClipLoader color="#6366f1" size={60} />
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/favorites" element={<FavouriteTravel />} />
                <Route path="/flight" element={<FlightList />} />
                <Route path="/packages" element={<TravelDestinationList />} />
                <Route path="/carrentals" element={<CarRentalList />} />
                <Route path="/hotels" element={<HotelBookingList />} />
                <Route path="/packages/:spotname" element={<TravelShowcase />} />
                <Route path="/flight-search" element={<FlightSearchPage />} />
                <Route path="/flight-results" element={<FlightResults />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-and-condition" element={<Terms />} />
                <Route path="/cancellation-policy" element={<CancellationPolicy />} />
                <Route path="/:slug" element={<RedirectEmbed />} />
              </Routes>
            </Suspense>
          </MainContent>
        </div>
      </main>
      {isAdminPage && <AdminLayout />}
      {showChrome && <SubFooter />}
      {showChrome && <Footer />}
    </>
  );
};

const Container = () => {
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setInitialLoading(false), 200);
    return () => clearTimeout(timer);
  }, []);

  if (initialLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <ClipLoader color="#6366f1" size={70} />
      </div>
    );
  }

  return <RouteWithLayout />;
};

export default Container;
